import React, { useRef, useState, useEffect } from 'react';
import { StyleSheet, View, Animated, PanResponder, Dimensions } from 'react-native';
import CustomLabel from '../../CustomLabel/CustomLabel';
import { useTheme } from '@react-navigation/native';

const CustomDraggableBox = (props) => {

    const [screenDimensions, setScreenDimensions] = useState(Dimensions.get('window'));

    useEffect(() => {
        Dimensions.addEventListener('change', ({ window }) => {
            setScreenDimensions(window);
        });
    }, []);

    // Calculate sizes dynamically based on current screen dimensions
    const BOX_SIZE_HEIGHT = 100;
    const BOX_SIZE_WIDTH = screenDimensions.width * 0.09;
    const ZONE_SIZE_HEIGHT = 100;
    const ZONE_SIZE_WIDTH = screenDimensions.width * 0.09;
    const ZONE_SPACING = screenDimensions.width * 0.02;

    const zones = Array.from({ length: 5 }).map((_, index) => ({
        x: ZONE_SPACING + (ZONE_SIZE_WIDTH + ZONE_SPACING) * index,
        y: 0,
    }));
    
    const { colors } = useTheme();
    const styles = makeStyles(colors, BOX_SIZE_WIDTH, BOX_SIZE_HEIGHT, ZONE_SIZE_WIDTH, ZONE_SIZE_HEIGHT, ZONE_SPACING);

    const [zoneStacks, setZoneStacks] = useState(Array(5).fill().map(() => []));
    // Each box knows which zone it's in, or null if not in a zone
    const [boxZones, setBoxZones] = useState(Array(1).fill(null));
    const [containerHeight, setContainerHeight] = useState(400);
    const [containerMarginTop, setContainerMarginTop] = useState(BOX_SIZE_HEIGHT + 50);
    const [boxPans, setBoxPans] = useState(undefined)
    //const { colors } = useTheme();
    const bPansRef = useRef([].map(pos => new Animated.ValueXY(pos)));

    // * Generate statring positions
    useEffect(() => {
        setBoxZones(Array(props.questionLines?.length).fill(null));
        let zStacks = Array(props.agreedisagree.length).fill().map(() => []);
        let tempInitial = [];
        props?.questionLines.map((qLItr, index) => {
            let pos = {};
            if (qLItr.question_line_resp !== '') {
                let position = 0;
                // * if submitted/saved  meaning response + empty zonestacks then set them
                if (zoneStacks.every(subArray => subArray.length === 0)) {
                    zStacks[parseInt(qLItr.question_line_resp)] = [...zStacks[parseInt(qLItr.question_line_resp)], parseInt(qLItr.question_line_order)];
                    position = zStacks[parseInt(qLItr.question_line_resp)].indexOf(parseInt(qLItr.question_line_order));
                }
                else {
                    // * Else set the moved place on refresh
                    zoneStacks[qLItr.question_line_resp]?.forEach((place, index) => {
                        if (qLItr.question_line_order === place) {
                            position = index;
                        }
                    })
                }
                let yOffset = zones[qLItr.question_line_resp].y + BOX_SIZE_HEIGHT + position * (BOX_SIZE_HEIGHT + 8) + 5;
                pos = {
                    x: ZONE_SPACING + (ZONE_SIZE_WIDTH + ZONE_SPACING) * (qLItr.question_line_resp), y: yOffset
                }
            }
            else
                pos = { x: ZONE_SPACING + (ZONE_SIZE_WIDTH + ZONE_SPACING) * (index), y: - BOX_SIZE_HEIGHT - ZONE_SPACING };
            tempInitial.push(pos)

        })
        // Overwrite the initial values in the ref
        bPansRef.current = tempInitial.map(pos => new Animated.ValueXY({ x: pos.x, y: pos.y }));
        if (zoneStacks.every(subArray => subArray.length === 0)) {
            setZoneStacks(zStacks)
        }
        setBoxPans(bPansRef.current)
    }, [props.questionLines])


    useEffect(() => {
        if (!boxZones || boxZones.length === 0) return;
        setContainerHeight(findLargestArray(zoneStacks) * BOX_SIZE_HEIGHT)
        // * If all question line is in zones (i.e. moved from starting position) 
        // * reduce the margin top origninal 50.
        if (calculateTotalArrayLengths(zoneStacks) === props.questionLines.length)
            setContainerMarginTop(50);
    }, [boxZones]);


    // * Helper function that returns the legnth of the zone with the most question line 
    // * this is used to amend the length of the question dynamically. 
    // * This is to avoid having empty spaces 
    const findLargestArray = (arrays) => {
        // Use reduce to find the maximum length
        return arrays.reduce((maxLength, currentArray) => {
            return currentArray.length > maxLength ? currentArray.length : maxLength;
        }, 0);
    }

    const calculateTotalArrayLengths = (arrays) => {
        // Use reduce to sum up the lengths of all arrays
        return arrays.reduce((totalLength, currentArray) => {
            return totalLength + currentArray.length;
        }, 0);
    }

    // * Helper: Find the nearest zone index
    const findNearestZone = (x, y) => {
        let minDist = Infinity;
        let minIdx = 0;
        zones.forEach((zone, idx) => {
            const dist = Math.sqrt(Math.pow(x - zone.x, 2) + Math.pow(y - zone.y, 2));
            if (dist < minDist) {
                minDist = dist;
                minIdx = idx;
            }
        });
        return minIdx;
    };

    // * Helper: Remove a box from all zone stacks
    const removeBoxFromZones = (boxIndex, stacks) =>
        stacks.map(stack => stack.filter(idx => idx !== boxIndex));

    // * Create a PanResponder for each box
    const panResponders = boxPans?.map((pan, boxIndex) =>
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onPanResponderGrant: () => {

                pan.setOffset({
                    x: pan.x._value,
                    y: pan.y._value,
                });
            },
            onPanResponderMove:
                Animated.event(
                    [null, { dx: pan.x, dy: pan.y }],
                    { useNativeDriver: false }
                )
            ,
            onPanResponderRelease: () => {
                pan.flattenOffset();

                const currentX = pan.x._value;
                const currentY = pan.y._value;

                // Find nearest zone
                const nearestZoneIdx = findNearestZone(currentX, currentY);

                setZoneStacks(prevStacks => {
                    // Remove this box from all stacks first
                    const newStacks = removeBoxFromZones(boxIndex, prevStacks);
                    // Add to the selected zone's stack
                    newStacks[nearestZoneIdx] = [...newStacks[nearestZoneIdx], boxIndex];

                    // Update boxZones mapping
                    setBoxZones(prevBoxZones => {
                        const newBoxZones = [...prevBoxZones];
                        newBoxZones[boxIndex] = nearestZoneIdx;
                        return newBoxZones;
                    });

                    // Calculate y offset for stacking
                    const stackPosition = newStacks[nearestZoneIdx]?.length - 1;
                    const yOffset = zones[nearestZoneIdx].y + BOX_SIZE_HEIGHT + stackPosition * (BOX_SIZE_HEIGHT + 8) + 5;
                    props.handleQuestionLineResp(nearestZoneIdx, props.questionLines[boxIndex].question_line_respUI, props.sectiond_id, props.questionId)

                    // Snap to zone (with stacking)
                    Animated.spring(pan, {
                        toValue: { x: zones[nearestZoneIdx].x, y: yOffset },
                        useNativeDriver: true,
                    }).start();

                    return newStacks;
                });


            },
        })
    );
    return (
        <View style={[styles.container, { height: containerHeight, marginTop: containerMarginTop }]}>
            {/* //* Render zones */ }
            {zones.map((zone, index) => (
                <View
                    key={`zone-${index}`}
                    style={[
                        styles.zone,
                        { left: zone.x, top: zone.y },
                        // Optionally, highlight if occupied
                        zoneStacks[index]?.length > 0 && index === 0 && { backgroundColor: '#ece0d1' },
                        zoneStacks[index]?.length > 0 && index === 1 && { backgroundColor: '#dbc1ac' },
                        zoneStacks[index]?.length > 0 && index === 2 && { backgroundColor: '#ceb4a1' },
                        zoneStacks[index]?.length > 0 && index === 3 && { backgroundColor: '#c2a493' },
                        zoneStacks[index]?.length > 0 && index === 4 && { backgroundColor: '#9d6e62' },

                    ]}
                >
                    <CustomLabel
                        value={props.agreedisagree[index]}
                        type='ASSMBOX'
                    />
                </View>
            ))}

            {/* // * Render movable boxes with question line name*/}
            {boxPans !== undefined && props.questionLines.map((qLItr, index) => {
                return (
                    props.isSubmitted ?
                        <Animated.View
                            key={`box-${index}`}
                            style={[
                                styles.box,
                                { transform: boxPans[index].getTranslateTransform(), left: 0, top: 0 },
                            ]}
                        >
                            <CustomLabel
                                value={qLItr.question_line_description}
                                type='ASSMBOX'
                            />
                        </Animated.View>
                        :
                        <Animated.View
                            key={`box-${index}`}
                            {...panResponders[index]?.panHandlers}
                            style={[
                                styles.box,
                                { transform: boxPans[index].getTranslateTransform(), left: 0, top: 0 },
                            ]}
                        >
                            <CustomLabel
                                value={qLItr.question_line_description}
                                type='ASSMBOX'
                            />
                        </Animated.View>)
            }
            )}
        </View>
    );
};

const makeStyles = (colors, BOX_SIZE_WIDTH, BOX_SIZE_HEIGHT, ZONE_SIZE_WIDTH, ZONE_SIZE_HEIGHT, ZONE_SPACING) => StyleSheet.create({
    container: {
        marginLeft: 50,
        marginRight: BOX_SIZE_WIDTH + ZONE_SPACING,
        marginBottom: 200,
    },
    box: {
        width: BOX_SIZE_WIDTH,
        height: BOX_SIZE_HEIGHT,
        backgroundColor: colors.buttons_primary,
        opacity: 0.9,
        borderRadius: 10,
        position: 'absolute',
    },
    zone: {
        width: ZONE_SIZE_WIDTH,
        height: ZONE_SIZE_HEIGHT,
        position: 'absolute',
        borderTopWidth: 0,
        borderLeftWidth: 2,
        borderRightWidth: 2,
        borderBottomWidth: 0,
        borderColor: '#64c864',
        borderRadius: 8,
    },
});

export default CustomDraggableBox;